import { ref, watch } from "vue";
import { getRefs } from "../utils";
import { watchDebounced } from "@vueuse/core";
import { Items } from "./items";
import { Reference, fetchReferences } from "./misc";
import { MultiSelect } from "./multi-select";
import { MultiRangeSlider } from "./multi-rangeslider";

interface ReferencesProps {
  rootEl: HTMLElement;
}

const References = (props: ReferencesProps) => {
  const references = ref<Reference[]>([]);
  const limit = ref(6);

  const searchString = ref("");

  const filterOptions = ref({
    filterConstructionTypes: "",
    filterBuildingTypes: "",
    filterProducts: "",
    filterLocations: "",
    filterCarriageWeights: "",
    filterHeights: "",
  });

  let initial = ref(true);
  let abortController: AbortController | null = null;

  watchDebounced(
    [searchString, filterOptions],
    async ([searchString, filterOptions]) => {
      try {
        if (abortController) abortController.abort();
        abortController = new AbortController();

        const response = await fetchReferences(
          {
            searchString: searchString,
            initial: initial.value,
            ...filterOptions,
          },
          abortController.signal
        );

        references.value = response;
        initial.value = false;
      } catch (e) {
        // ignore
      }
    },
    { immediate: true, deep: true, debounce: () => (initial.value ? 0 : 300) }
  );

  const refs = getRefs<{
    search: HTMLElement;
    viewMore: HTMLElement;
    items: HTMLElement;
    constructionTypes: HTMLElement;
    buildingTypes: HTMLElement;
    products: HTMLElement;
    locations: HTMLElement;
    carriageWeights: HTMLElement;
    heights: HTMLElement;
    moreFilters: HTMLElement;
    count: HTMLElement;
  }>(props.rootEl);

  refs.search.addEventListener("input", (e) => {
    if (e.target instanceof HTMLInputElement) {
      searchString.value = e.target.value;
    }
  });

  refs.viewMore.addEventListener("click", () => {
    limit.value += 6;
  });

  watch(
    references,
    (references) => {
      const title = references.length === 1 ? "Referenz" : "Referenzen";

      refs.count.innerText = `${references.length.toString()} ${title} gefunden`;
    },
    { immediate: true, deep: true }
  );

  Items({ rootEl: refs.items, references, limit, buttonEl: refs.viewMore });

  if (window.vd_bundle__filter) {
    const filterSettings = window.vd_bundle__filter;

    MultiSelect({
      rootEl: refs.constructionTypes,
      options: filterSettings.constructiontype.map((item) => ({
        id: item.uid.toString(),
        label: item.name,
      })),
      onSelectionChange: (selection) => {
        filterOptions.value.filterConstructionTypes = selection;
      },
    });

    MultiSelect({
      rootEl: refs.buildingTypes,
      options: filterSettings.buildingtype.map((item) => ({
        id: item.uid.toString(),
        label: item.name,
      })),
      onSelectionChange: (selection) => {
        filterOptions.value.filterBuildingTypes = selection;
      },
    });

    MultiSelect({
      rootEl: refs.products,
      options: filterSettings.product.map((item) => ({
        id: item.uid.toString(),
        label: item.name,
      })),
      onSelectionChange: (selection) => {
        filterOptions.value.filterProducts = selection;
      },
    });

    MultiSelect({
      rootEl: refs.locations,
      options: filterSettings.location.map((item) => ({
        id: item.uid.toString(),
        label: item.name,
      })),
      onSelectionChange: (selection) => {
        filterOptions.value.filterLocations = selection;
      },
    });

    refs.moreFilters.addEventListener("click", () => {
      refs.carriageWeights.style.display = "block";
      refs.heights.style.display = "block";
    });

    MultiRangeSlider({
      rootEl: refs.carriageWeights,
      min: filterSettings.min_nutzlast,
      max: filterSettings.max_nutzlast,
      minDistance: Math.floor(filterSettings.max_nutzlast / 20),
      suffix: " kg",
      onChange: (min, max) => {
        filterOptions.value.filterCarriageWeights = `${min} kg,${max} kg`;
      },
    });

    MultiRangeSlider({
      rootEl: refs.heights,
      min: filterSettings.min_foerderhoehe,
      max: filterSettings.max_foerderhoehe,
      minDistance: Math.floor(filterSettings.max_foerderhoehe / 20),
      suffix: " m",
      onChange: (min, max) => {
        filterOptions.value.filterHeights = `${min} m,${max} m`;
      },
    });
  }
};

const init = () => {
  const filterDataElement = document.getElementById("vd_bundle__filter");
  const filterData = JSON.parse(filterDataElement?.textContent || "");
  window.vd_bundle__filter = filterData;

  const referencesComps = document.querySelectorAll<HTMLElement>("[h-comp='references']");

  for (const references of referencesComps) {
    References({ rootEl: references });
  }
};

init();
